<template>
  <div class="relative" ref="rootEl">
    <!-- Chip — shows a calendar icon + the current range label. Active
         (popover open) gets a stronger ring so it reads as the focused
         filter. -->
    <button
      type="button"
      @click="open = !open"
      :class="open
        ? 'border-purple-500 ring-2 ring-purple-500/20 bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-200'
        : 'border-gray-300 dark:border-white/10 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 hover:border-purple-500/50'"
      class="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium shadow-sm transition-colors"
    >
      <svg class="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
      <span class="text-[10px] uppercase tracking-wider text-gray-400">Date</span>
      <span class="text-gray-900 dark:text-white">{{ rangeLabel }}</span>
      <svg class="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <!-- Popover — preset list on the left, custom range on the right.
         Mounted inline (not Teleported) since the bar lives at the top
         of the page where it has plenty of room below. -->
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="open"
        class="absolute left-0 top-full mt-2 z-30 w-[480px] rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-2xl flex"
      >
        <!-- Presets -->
        <div class="w-44 border-r border-gray-200 dark:border-white/10 py-2">
          <button
            v-for="p in presets"
            :key="p.id"
            type="button"
            @click="selectPreset(p.id)"
            :class="modelValue.presetId === p.id
              ? 'bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-200 font-semibold'
              : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5'"
            class="w-full text-left px-3 py-1.5 text-sm transition-colors"
          >
            {{ p.label }}
          </button>
          <div class="my-1 border-t border-gray-200 dark:border-white/10"></div>
          <button
            type="button"
            @click="enterCustomMode"
            :class="modelValue.presetId === 'custom'
              ? 'bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-200 font-semibold'
              : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5'"
            class="w-full text-left px-3 py-1.5 text-sm transition-colors"
          >
            Custom range…
          </button>
        </div>

        <!-- Right pane: custom range editor (always visible — preset
             clicks just preview the dates here). -->
        <div class="flex-1 p-4 space-y-3">
          <div>
            <label class="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">From</label>
            <input
              type="date"
              :value="customStartIso"
              @input="onCustomStartInput(($event.target as HTMLInputElement).value)"
              class="glass-input w-full px-3 py-2 text-sm font-mono"
            />
          </div>
          <div>
            <label class="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">To</label>
            <input
              type="date"
              :value="customEndIso"
              @input="onCustomEndInput(($event.target as HTMLInputElement).value)"
              class="glass-input w-full px-3 py-2 text-sm font-mono"
            />
          </div>
          <p v-if="error" class="text-xs text-red-500">{{ error }}</p>
          <div class="flex justify-end gap-2 pt-1">
            <button
              type="button"
              @click="open = false"
              class="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >Cancel</button>
            <button
              type="button"
              @click="applyCustom"
              :disabled="!!error || !customStartIso || !customEndIso"
              class="px-4 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-purple-600 to-blue-500 text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >Apply</button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import {
  DATE_RANGE_PRESETS,
  buildPresetRange,
  formatDateRangeLabel,
  type DateRange,
  type DateRangePresetId,
} from '~/composables/useDashboardFilters'

const props = defineProps<{ modelValue: DateRange }>()
const emit = defineEmits<{ (e: 'update:modelValue', value: DateRange): void }>()

const open = ref(false)
const rootEl = ref<HTMLElement | null>(null)

const presets = DATE_RANGE_PRESETS
const rangeLabel = computed(() => formatDateRangeLabel(props.modelValue))

// ---------------------------------------------------------------------------
// Preset selection — click immediately applies + closes the popover.
// ---------------------------------------------------------------------------

function selectPreset(id: DateRangePresetId) {
  const next = buildPresetRange(id)
  emit('update:modelValue', next)
  open.value = false
}

// ---------------------------------------------------------------------------
// Custom range — the right-pane date inputs operate on local YYYY-MM-DD
// strings. We keep them synced with the current modelValue so opening the
// popover on a preset shows the corresponding dates pre-filled.
// ---------------------------------------------------------------------------

function toIsoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const customStartIso = ref(toIsoDate(props.modelValue.start))
const customEndIso = ref(toIsoDate(props.modelValue.end))

watch(() => props.modelValue, (v) => {
  customStartIso.value = toIsoDate(v.start)
  customEndIso.value = toIsoDate(v.end)
})

function onCustomStartInput(v: string) { customStartIso.value = v }
function onCustomEndInput(v: string)   { customEndIso.value = v }

const error = computed(() => {
  if (!customStartIso.value || !customEndIso.value) return ''
  const s = new Date(customStartIso.value + 'T00:00:00')
  const e = new Date(customEndIso.value + 'T23:59:59')
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return 'Invalid date.'
  if (s > e) return 'Start must be before end.'
  return ''
})

function enterCustomMode() {
  // Click the "Custom range…" preset row — switch the modelValue's
  // presetId to 'custom' so the right-pane dates become editable in
  // their own right (without applying yet).
  emit('update:modelValue', {
    start: props.modelValue.start,
    end: props.modelValue.end,
    presetId: 'custom',
  })
}

function applyCustom() {
  if (error.value) return
  const start = new Date(customStartIso.value + 'T00:00:00')
  const end = new Date(customEndIso.value + 'T23:59:59')
  emit('update:modelValue', { start, end, presetId: 'custom' })
  open.value = false
}

// ---------------------------------------------------------------------------
// Click-outside to close.
// ---------------------------------------------------------------------------

function onDocumentClick(e: MouseEvent) {
  if (!open.value) return
  if (!rootEl.value) return
  if (e.target instanceof Node && rootEl.value.contains(e.target)) return
  open.value = false
}
onMounted(() => document.addEventListener('mousedown', onDocumentClick))
onBeforeUnmount(() => document.removeEventListener('mousedown', onDocumentClick))
</script>
